import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useToast, useVersionCheck } from '@retainerclub/hooks';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';

function Toast() {
  const {
    toast: { title, message, show, type, onAction, actionText },
    setToast,
  } = useToast();

  useVersionCheck();

  const { state } = useLocation();

  useEffect(() => {
    if (state?.showToast) {
      setToast({
        show: true,
        message: state.message,
        title: state.title,
        type: 'success',
      });
    }
  }, [state?.message, state?.showToast, state?.title, setToast]);

  const dismissToast = useCallback(() => {
    setToast({
      show: false,
      message: '',
      title: '',
      type: '',
      onAction: null,
      actionText: null,
    });
  }, [setToast]);

  const handleAction = useCallback(() => {
    if (onAction) {
      onAction();
    }
    dismissToast();
  }, [onAction, dismissToast]);

  useEffect(() => {
    // Only auto-dismiss if there's no action button
    if (!show || (onAction && actionText)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      dismissToast();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [dismissToast, show, onAction, actionText]);

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6 sm:pt-16"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition show={show}>
          <div
            className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg ${type === 'success' && 'bg-white'} ${type === 'error' && 'bg-red-50'} ${type === 'info' && 'bg-rcprimary-50'} shadow-lg ring-1 ring-black/5 transition duration-300 ease-in-out data-closed:translate-y-2 data-enter:translate-y-0 data-closed:opacity-0 data-enter:opacity-100 data-leave:opacity-0 sm:data-closed:translate-x-2 sm:data-enter:translate-x-0 sm:data-closed:translate-y-0`}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">
                  {type === 'success' && (
                    <CheckCircleIcon
                      className="size-6 text-green-400"
                      aria-hidden="true"
                    />
                  )}
                  {type === 'error' && (
                    <ExclamationTriangleIcon
                      className="size-6 text-rccallouts-danger"
                      aria-hidden="true"
                    />
                  )}
                  {type === 'info' && (
                    <InformationCircleIcon
                      className="size-6 text-rcprimary-600"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="font-medium text-gray-900 text-sm">{title}</p>
                  <p className="mt-1 text-gray-500 text-sm">{message}</p>
                  {actionText && onAction && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md bg-rcprimary-600 px-3 py-2 font-semibold text-sm text-white shadow-sm hover:bg-rcprimary-500 focus-visible:outline-2 focus-visible:outline-rcprimary-600 focus-visible:outline-offset-2"
                        onClick={handleAction}
                      >
                        {actionText}
                      </button>
                    </div>
                  )}
                </div>
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-rcprimary-500 focus:ring-offset-2"
                    onClick={() => {
                      dismissToast();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="size-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}

export default Toast;
